import { initPixel, trackEventSingle } from "../facebookPixel";
import { getOrCreateEventId } from './helpers/trackingSessionManager';
import { createEventObject, checkTrackingConversion } from "./helpers/util"; // Adjust path if necessary

/**
 * Maps customer data to Meta Pixel's Advanced Matching parameters.
 * Returns an object with `fbq` compatible keys, or `null` if all input fields are null/empty.
 *
 * @param {object} customerData - The customer data object.
 * @param {string | null} customerData.email - Customer's email.
 * @param {string | null} customerData.phone - Customer's phone number (e.g., '628123456789').
 * @param {string | null} customerData.name - Customer's full name.
 * @returns {object | null} An object with Meta Pixel advanced matching parameters (em, ph, fn, ln) or null.
 */
function mapCustomerData(customerData) {
  const mappedData = {};
  let hasData = false;

  // Email
  if (customerData.email) {
    mappedData.em = String(customerData.email).trim().toLowerCase();
    hasData = true;
  }

  // Phone (clean and format as digits only, include country code if possible)
  if (customerData.phone) {
    // Remove non-numeric characters for phone
    const cleanedPhone = String(customerData.phone).replace(/\D/g, '');
    if (cleanedPhone) {
      mappedData.ph = cleanedPhone;
      hasData = true;
    }
  }

  // Name (split into first and last name if possible)
  if (customerData.name) {
    const nameParts = String(customerData.name).trim().toLowerCase().split(' ');
    if (nameParts.length > 0 && nameParts[0]) {
      mappedData.fn = nameParts[0]; // First name
      hasData = true;
    }
    if (nameParts.length > 1 && nameParts[nameParts.length - 1]) {
      mappedData.ln = nameParts[nameParts.length - 1]; // Last name
      hasData = true;
    }
  }

  return hasData ? mappedData : null;
}
/**
 * Handles the Facebook Pixel tracking for all configured pixels and events.
 * This function integrates event ID, event time, fbp, fbc, and advanced matching data.
 * @param {Array<object>} fbTracking - An array of Facebook Pixel configurations.
 * @param {object} data - The data object containing fbp, fbc, event_id, event_time, customerData, and other page/product data.
 */
export async function handleFacebookPixelTracking(fbTracking, data, event_key, globalConversionSetting) {
  if (!fbTracking || fbTracking.length === 0) {
    console.warn("No Facebook Pixel configurations found in tracking object.");
    return;
  }

  // Prepare customer data for Advanced Matching
  let userData
  if (data.customerData) {
    userData = await mapCustomerData(data.customerData);
  }

  for (const pixel of fbTracking) {
    const pixelId = pixel.pixelId ? pixel.pixelId.trim() : "";

    // Initialize the pixel once per pixelId.
    // The `initPixel` function itself has internal checks to prevent re-initialization.
    initPixel(pixelId, userData);

    if (!pixel.pixelEvents || pixel.pixelEvents.length === 0) {
      console.warn(`No events configured for Facebook Pixel ID: ${pixelId}`);
      continue;
    }

    if (pixel.pixelEvents && pixel.pixelEvents.length) {
      for (const event of pixel.pixelEvents) {
        const eventName = event.name ? event.name.trim() : "";
        if (!eventName) {
          console.warn(
            `Skipping pixel event due to missing event name for Pixel ID: ${pixelId}`,
            event
          );
          continue;
        }

        // Construct a unique key for session-based conversion checking.
        const conversionKey = `pixelsFB_${pixelId}_${eventName}`;
        const shouldProcess = await checkTrackingConversion(
          pixel,
          conversionKey,
          globalConversionSetting,
          data.event_key
        );
        if (!shouldProcess) {
          console.log(
            `Event "${eventName}" for Pixel ID "${pixelId}" skipped based on conversion rules.`
          );
          continue;
        }

        // Create the event data object using the helper function.
        const eventData = createEventObject(eventName, data);
        const event_id = getOrCreateEventId(eventName, data.id, event_key, data.event_key);
        // const event_time = getOrCreateEventTime(); // Unix timestamp in seconds

        // Construct the options object for trackEventSingle to include deduplication and advanced matching data
        const options = {
          eventID: event_id, // Unique event ID for deduplication
        };

        // Track the event for the specific pixel ID.
        trackEventSingle(pixelId, eventName, eventData, options);
      }
    } 
  }
}
